/**
 * useFunnelBeacon — React glue around `@proof/client-core/analytics`.
 *
 * Mounts two things for an authenticated session, and nothing at all for an
 * unauthenticated one (the endpoint is auth-required; beaconing before sign-in
 * would produce nothing but 401s):
 *
 *   1. One `session_start` beacon per browser session, carrying the CSS
 *      display-mode — the standalone-vs-browser split. This is also what
 *      triggers the server-side `w1_return` evaluation for a returning user
 *      whose session cookie is still live and who therefore never re-logs-in.
 *   2. A window `appinstalled` listener that fires `pwa_installed` once.
 *
 * Everything else in the funnel is emitted server-side from the authoritative
 * mutation point — this hook cannot and does not claim any milestone.
 *
 * Failures are silent by design: there is no user action behind a beacon, so
 * there is nothing to surface (this is not a Rule 8 surface). The transport
 * itself swallows and logs nothing — see the beacon module's invariant 1.
 *
 * Plan ref: .plan/features/2026-07-24-r2-funnel-activation-instrumentation.md §3
 */

import { useEffect } from "react";
import {
  sendFunnelBeacon,
  sendSessionStartOnce,
  detectDisplayMode,
} from "@proof/client-core/analytics";

/**
 * @param enabled — true once the session is confirmed authenticated. The hook
 *                  is called unconditionally (hooks rules) and no-ops while
 *                  false, so it can sit above the route's early returns.
 */
export function useFunnelBeacon(enabled: boolean): void {
  // Session-start beacon — once per browser session (guarded in sessionStorage
  // by the client-core helper, so a remount or a StrictMode double-invoke sends
  // one beacon, not two).
  useEffect(() => {
    if (!enabled) return;
    void sendSessionStartOnce();
  }, [enabled]);

  // PWA install — the `appinstalled` event is the only signal that the user
  // added proof. to their home screen. Deduped server-side (once per user), so
  // a listener that fires on more than one device still counts once.
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;
    const onInstalled = (): void => {
      void sendFunnelBeacon("pwa_installed", detectDisplayMode());
    };
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, [enabled]);
}
